/**
 * Platillos con peso final (Req. 20.3, 20.4; design.md, 8.11).
 *
 * - Los valores salen de los ingredientes con sus valores ACTUALES: si cambia
 *   un ingrediente, cambian los registros futuros; los anteriores conservan su
 *   copia (Req. 18.4, 20.4).
 * - Por ración = todo el platillo ÷ raciones. Con el peso final cocinado,
 *   también por gramos: un gramo del platillo cocinado = todo ÷ peso final.
 * - Un dato desconocido de un ingrediente deja desconocido ese dato del
 *   platillo: nunca se suma como cero.
 */

import type { Dish, Food, IngredientePlatillo, Nutrientes } from '@/db/types';
import {
  calculableDeBase,
  calculableDePropio,
  calcular,
  multiplicar,
  type AlimentoBase,
  type Calculable,
} from './nutricion';
import { ErrorValidacion } from './errors';

/** El alimento de un ingrediente, tal como está hoy. */
export interface AlimentoDeIngrediente {
  nombre: string;
  calculable: Calculable;
  rompeAyuno: boolean;
}

/** Busca el alimento de un ingrediente tal como está hoy; null si ya no existe. */
export type ResolverIngrediente = (i: IngredientePlatillo) => AlimentoDeIngrediente | null;

/**
 * Los ingredientes salen de la base incluida o de Mis alimentos. Un alimento
 * propio archivado sigue sirviendo: archivar solo lo quita del buscador.
 */
export function crearResolver(
  baseDe: (id: string) => AlimentoBase | undefined,
  propios: ReadonlyMap<string, Food>,
): ResolverIngrediente {
  return (i) => {
    if (i.origen === 'base') {
      const a = baseDe(i.alimentoId);
      return a === undefined ? null : { nombre: a.nombre, calculable: calculableDeBase(a), rompeAyuno: a.rompeAyuno };
    }
    const f = propios.get(i.alimentoId);
    return f === undefined ? null : { nombre: f.nombre, calculable: calculableDePropio(f), rompeAyuno: f.rompeAyuno };
  };
}

export interface IngredienteCalculado {
  ingrediente: IngredientePlatillo;
  nombre: string | null;
  valores: Nutrientes | null;
  gramos: number | null;
  error: string | null;
}

export interface CalculoPlatillo {
  ingredientes: IngredienteCalculado[];
  /** Todo el platillo; null si algún ingrediente no se puede calcular. */
  total: Nutrientes | null;
  porRacion: Nutrientes | null;
  /** Suma de los gramos de los ingredientes, si todos los tienen. */
  gramosIngredientes: number | null;
  /** Rompe el ayuno si lo rompe algún ingrediente (o si alguno ya no existe). */
  rompeAyuno: boolean;
  errores: string[];
}

/** Suma nutrientes: las kcal siempre; un macro desconocido en uno queda desconocido. */
export function sumarNutrientes(lista: readonly Nutrientes[]): Nutrientes {
  const suma = (f: (n: Nutrientes) => number | null) =>
    lista.some((n) => f(n) === null) ? null : lista.reduce((s, n) => s + (f(n) ?? 0), 0);
  return {
    kcal: lista.reduce((s, n) => s + n.kcal, 0),
    proteinaG: suma((n) => n.proteinaG),
    carbohidratosG: suma((n) => n.carbohidratosG),
    grasaG: suma((n) => n.grasaG),
    fibraG: suma((n) => n.fibraG),
  };
}

export function calcularPlatillo(
  platillo: Pick<Dish, 'raciones' | 'ingredientes'>,
  resolver: ResolverIngrediente,
): CalculoPlatillo {
  let rompeAyuno = platillo.ingredientes.length === 0;
  const ingredientes: IngredienteCalculado[] = platillo.ingredientes.map((ingrediente) => {
    const alimento = resolver(ingrediente);
    if (alimento === null) {
      rompeAyuno = true;
      return { ingrediente, nombre: null, valores: null, gramos: null, error: 'Un ingrediente ya no existe: quítalo o cámbialo.' };
    }
    if (alimento.rompeAyuno) rompeAyuno = true;
    const r = calcular(alimento.calculable, ingrediente.cantidad, ingrediente.porcion);
    if (!r.ok) return { ingrediente, nombre: alimento.nombre, valores: null, gramos: null, error: `${alimento.nombre}: ${r.error}` };
    return { ingrediente, nombre: alimento.nombre, valores: r.valores, gramos: r.gramos, error: null };
  });
  const errores = ingredientes.flatMap((i) => (i.error === null ? [] : [i.error]));
  const valores = ingredientes.flatMap((i) => (i.valores === null ? [] : [i.valores]));
  const total = errores.length > 0 || valores.length === 0 ? null : sumarNutrientes(valores);
  const gramos = ingredientes.map((i) => i.gramos);
  return {
    ingredientes,
    total,
    porRacion: total === null || !(platillo.raciones > 0) ? null : multiplicar(total, 1 / platillo.raciones),
    gramosIngredientes: gramos.length > 0 && gramos.every((g) => g !== null) ? gramos.reduce((s: number, g) => s + (g ?? 0), 0) : null,
    rompeAyuno,
    errores,
  };
}

export const RACION = 'ración';
export const MAXIMO_INGREDIENTES = 50;

/** El peso final si es un número válido; ausente (esquema 2 sin él) o raro = null. */
export function pesoFinalDe(platillo: Pick<Dish, 'pesoFinalG'>): number | null {
  const peso = platillo.pesoFinalG;
  return typeof peso === 'number' && Number.isFinite(peso) && peso > 0 ? peso : null;
}

/**
 * Para registrarlo como cualquier alimento: por ración y, con el peso final,
 * también en gramos del platillo cocinado. null si no se puede calcular.
 */
export function calculableDePlatillo(platillo: Pick<Dish, 'raciones' | 'pesoFinalG'>, calculo: CalculoPlatillo): Calculable | null {
  if (calculo.porRacion === null) return null;
  const peso = pesoFinalDe(platillo);
  return {
    referencia: { tipo: 'porPorcion', porcion: { nombre: RACION, gramos: peso === null ? null : peso / platillo.raciones } },
    nutrientes: calculo.porRacion,
    porciones: [],
  };
}

/** Por 100 g del platillo cocinado; null sin peso final. */
export function por100gDePlatillo(platillo: Pick<Dish, 'pesoFinalG'>, calculo: CalculoPlatillo): Nutrientes | null {
  const peso = pesoFinalDe(platillo);
  if (calculo.total === null || peso === null) return null;
  return multiplicar(calculo.total, 100 / peso);
}

export function validarPlatillo(p: Dish): void {
  const nombre = p.nombre.trim();
  if (nombre === '') throw new ErrorValidacion('Escribe el nombre del platillo.', 'nombre');
  if (nombre.length > 80) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');
  if (!(Number.isFinite(p.raciones) && p.raciones > 0 && p.raciones <= 100)) {
    throw new ErrorValidacion('Las raciones deben ser más de 0 y hasta 100.', 'raciones');
  }
  const peso = p.pesoFinalG ?? null;
  if (peso !== null && !(typeof peso === 'number' && Number.isFinite(peso) && peso > 0 && peso <= 20_000)) {
    throw new ErrorValidacion('El peso final debe ser mayor que cero y hasta 20,000 g.', 'pesoFinalG');
  }
  if (p.ingredientes.length === 0) throw new ErrorValidacion('Agrega al menos un ingrediente.', 'ingredientes');
  if (p.ingredientes.length > MAXIMO_INGREDIENTES) {
    throw new ErrorValidacion(`Un platillo puede tener hasta ${MAXIMO_INGREDIENTES} ingredientes.`, 'ingredientes');
  }
  for (const i of p.ingredientes) {
    if (!(Number.isFinite(i.cantidad) && i.cantidad > 0 && i.cantidad <= 20_000)) {
      throw new ErrorValidacion('Cada ingrediente necesita una cantidad mayor que cero y hasta 20,000.', 'ingredientes');
    }
    if (i.porcion.trim() === '') throw new ErrorValidacion('Elige la porción de cada ingrediente.', 'ingredientes');
  }
}
